import React, { useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import Reveal from '../ui/Reveal';
import WordReveal from '../ui/WordReveal';
import { LocationIcon, MailIcon, PhoneIcon } from '../ui/icons';
import { site, contact, contactCopy } from '../data/site';

/**
 * The consultation form.
 *
 * There is no contact endpoint on the API, so the previous build called
 * alert("Form submitted successfully!") and dropped the message on the
 * floor. Rather than keep saying that, submit composes a mail to the
 * office from the fields and hands it to the reader's mail client, which
 * is a delivery path that actually exists. The copy under the button
 * says so, so nobody is told their enquiry was sent when it was not.
 *
 * Swapping this for a POST later is a one-function change: replace the
 * body of `deliver`.
 */
function deliver({ name, email, phone, message }) {
    const subject = `Consultation request from ${name}`;
    const body = [
        `Name:  ${name}`,
        `Email: ${email}`,
        `Phone: ${phone}`,
        '',
        message || '(no details given)'
    ].join('\n');

    window.location.href =
        `mailto:${contact.email}` +
        `?subject=${encodeURIComponent(subject)}` +
        `&body=${encodeURIComponent(body)}`;
}

export default function ContactSection() {
    const [verified, setVerified] = useState(false);
    const [sent, setSent] = useState(false);

    // CRA inlines the key at build time; without it reCAPTCHA cannot render,
    // so the gate is skipped rather than leaving an unsubmittable form.
    const siteKey = process.env.REACT_APP_GOOGLE_RECAPTCHA_KEY;
    const gated = Boolean(siteKey);

    const onSubmit = event => {
        event.preventDefault();

        if (gated && !verified) return;

        const data = new FormData(event.currentTarget);

        deliver({
            name: data.get('name'),
            email: data.get('email'),
            phone: data.get('phone'),
            message: data.get('message')
        });

        setSent(true);
    };

    return (
        <section className="contact" id="contact">
            <div className="contact__glow" aria-hidden="true" />

            <div className="shell contact__inner">
                <div className="contact__aside">
                    <Reveal y={18}>
                        <span className="t-eyebrow t-eyebrow--accent">{contactCopy.eyebrow}</span>
                    </Reveal>

                    <WordReveal as="h2" className="t-h2" text={contactCopy.title} />

                    <Reveal delay={0.14}>
                        <p className="t-lead contact__lead">
                            {contactCopy.lead}
                        </p>
                    </Reveal>

                    <div className="contact__details">
                        <Reveal className="contact__detail" delay={0.2} y={20}>
                            <span className="contact__detail-icon"><PhoneIcon /></span>
                            <span>
                                <span className="contact__detail-label">Phone</span>
                                <a className="link" href={contact.phoneHref}>{contact.phone}</a>
                            </span>
                        </Reveal>

                        <Reveal className="contact__detail" delay={0.26} y={20}>
                            <span className="contact__detail-icon"><MailIcon /></span>
                            <span>
                                <span className="contact__detail-label">Email</span>
                                <a className="link" href={`mailto:${contact.email}`}>{contact.email}</a>
                            </span>
                        </Reveal>

                        <Reveal className="contact__detail" delay={0.32} y={20}>
                            <span className="contact__detail-icon"><LocationIcon /></span>
                            <span>
                                <span className="contact__detail-label">Headquarters</span>
                                <a
                                    className="link"
                                    href={contact.addressHref}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    {contact.address}
                                </a>
                                <span className="t-small" style={{ display: 'block', marginTop: '0.5rem' }}>
                                    {contact.serviceArea}
                                </span>
                            </span>
                        </Reveal>
                    </div>
                </div>

                <Reveal delay={0.1} y={30}>
                    <form className="form" onSubmit={onSubmit}>
                        <p className="form__note">
                            Schedule a complimentary, no-obligation consultation with{' '}
                            {site.name} by completing the form below.
                        </p>

                        <div className="field">
                            <label className="field__label" htmlFor="contact-name">Your name</label>
                            <input id="contact-name" name="name" type="text"
                                autoComplete="name" placeholder="Jane Rivera" required />
                        </div>

                        <div className="field">
                            <label className="field__label" htmlFor="contact-email">Your email</label>
                            <input id="contact-email" name="email" type="email"
                                autoComplete="email" placeholder="jane@example.com" required />
                        </div>

                        <div className="field">
                            <label className="field__label" htmlFor="contact-phone">Your phone number</label>
                            <input id="contact-phone" name="phone" type="tel"
                                autoComplete="tel" placeholder="(555) 000-0000" required />
                        </div>

                        <div className="field">
                            <label className="field__label" htmlFor="contact-message">
                                How can we help? (optional)
                            </label>
                            <textarea id="contact-message" name="message"
                                placeholder="Scope, address, rough timeline…" />
                        </div>

                        <p className="form__note">
                            By submitting your phone number you agree to receive SMS messages.
                            Message and data rates may apply. Reply STOP to unsubscribe. Never
                            share sensitive information (card numbers, social security numbers,
                            passwords) through this form.
                        </p>

                        {gated && (
                            <div className="form__captcha">
                                {/* Google ships a dark variant of the widget;
                                    it is the only way to restyle it, since the
                                    thing renders in a cross-origin iframe. */}
                                <ReCAPTCHA
                                    sitekey={siteKey}
                                    theme="dark"
                                    onChange={value => setVerified(Boolean(value))}
                                />
                            </div>
                        )}

                        <button
                            className="btn btn--accent"
                            type="submit"
                            disabled={gated && !verified}
                        >
                            Open my request
                            <span className="btn__arrow" aria-hidden="true">→</span>
                        </button>

                        <p className="form__note">
                            This opens a pre-filled message in your email app addressed to{' '}
                            {contact.email}. Nothing is sent until you press send there.
                            Prefer to talk? Call{' '}
                            <a className="link" href={contact.phoneHref}>{contact.phone}</a>.
                        </p>

                        {sent && (
                            <p className="form__status form__status--ok" role="status">
                                Your email app should have opened with the request ready to send.
                                If it did not, email us directly at{' '}
                                <a className="link" href={`mailto:${contact.email}`}>{contact.email}</a>.
                            </p>
                        )}
                    </form>
                </Reveal>
            </div>
        </section>
    );
}
